import { vec2 } from "gl-matrix"
import { Texture } from "./backends/webgl/texture"
import { BrushStroke, adjustBrushSpacingByFeather } from "./utils/brushing"
import {
	HealingAiInputNormalization,
	HealingGeneratedSource,
	HealingItem,
	Rectangle,
} from "./types"
import { Renderer } from "./pipeline/renderer"
import { DualBuffers } from "./pipeline/dual-buffers"
import { TexturesPool } from "./utils/textures-pool"
import { copyFrom, copyTo } from "./utils/texture-copy"
import { clamp } from "./utils/math"
import { equals, rectEquals, rectOverlap, rectSizeEquals } from "@/utils/math"
import { MaskPainter } from "./utils/mask-painter"
import {
	clampAlignmentAtEdges,
	convertAlignmentToUnoriented,
	getHealingRenderDataId,
	isNearEdge,
} from "@/store/editor/healing-utils"
import {
	createHealingTransformBoundsContext,
	getHealingTransformScissorBounds,
	HealingTransformBoundsContext,
	ScissorBounds,
} from "./utils/healing-transform-bounds"

export interface HealingOptions {
	cacheEnabled: boolean
}

interface HealingRenderData {
	id: string
	strokes: BrushStroke[]
	resultPatch: Texture | null
	mask: Texture | null
	destination: Rectangle
	source: Rectangle
	aiSource: HealingGeneratedSource | null
	applyBrushMask?: boolean // Whether to use brush strokes as mask or full rectangle
	applyAlignment?: boolean // Whether to apply optical flow alignment correction
	alignment?: { dx: number; dy: number; confidence: number; scaleX?: number; scaleY?: number }
	transformBoundsCache?: { key: string; bounds: ScissorBounds | null }
}

interface HealingCompositeTransform {
	boundsContext: HealingTransformBoundsContext
	lensUniforms: ReturnType<Renderer["profileManager"]["getLensUniforms"]>
	chromaticAberrationUniforms: ReturnType<
		Renderer["profileManager"]["getChromaticAberrationUniforms"]
	>
}

interface AiSourceData {
	bitmap: ImageBitmap
	width: number
	height: number
}

const getRectDimension = (a: Rectangle, width: number, height: number): vec2 => {
	return [Math.round(a.width * width), Math.round(a.height * height)]
}

/**
 * GPU-accelerated healing/clone/AI-fill renderer.
 *
 * All computation happens in **uncropped image space** (before orientation/crop/straighten).
 * Each healing patch goes through: source extraction → Poisson blending → masked compositing.
 *
 * Two alignment mechanisms exist and are independent:
 * - **Bitmap alignment** (optical-flow): shifts/scales the decoded AI bitmap itself while
 *   keeping the destination rect and mask unchanged. Applied during `decodeAiSource`.
 * - **Destination rect change** (user drag): moves the rect where the patch is composited.
 *   The bitmap content is unchanged; only its placement on the canvas moves.
 */
export class HealingRenderer {
	private healings: HealingRenderData[] = []
	private renderer: Renderer

	private width = 0
	private height = 0

	private maskPainter: MaskPainter

	private texturesPool: TexturesPool
	private masksPool: TexturesPool
	private needCompute = false
	private aiSources = new Map<string, AiSourceData>()
	private pendingAiSources = new Set<string>()

	constructor(renderer: Renderer) {
		this.renderer = renderer

		this.maskPainter = new MaskPainter(this.renderer, true)
		this.texturesPool = new TexturesPool(renderer.backend, "rgba16float")
		this.masksPool = new TexturesPool(renderer.backend, "rgba8unorm")
	}

	setAiSource(id: string, data: AiSourceData) {
		this.aiSources.set(id, data)
		this.needCompute = true
	}

	removeAllAiSources() {
		this.aiSources.clear()
		this.needCompute = true
	}

	// Helper method to get the currently selected AI source from a HealingItem
	private getSelectedAiSourceFromItem(item: HealingItem): HealingGeneratedSource | null {
		if (!item.aiVariations || item.aiVariations.length === 0) {
			return null
		}
		const selectedIndex = item.selectedVariation || 0
		return item.aiVariations[selectedIndex] || null
	}

	/** Pre-decode AI-generated bitmaps for all healing items that don't have cached data yet. */
	async hydrateAiSources(healings: HealingItem[]) {
		const tasks = healings
			.filter((healing) => {
				const aiSource = this.getSelectedAiSourceFromItem(healing)
				// Use cache ID that includes variation + settings
				const cacheId = getHealingRenderDataId(healing)
				return (
					aiSource && !this.aiSources.has(cacheId) && !this.pendingAiSources.has(cacheId)
				)
			})
			.map(async (healing) => {
				const aiSource = this.getSelectedAiSourceFromItem(healing) as HealingGeneratedSource
				const cacheId = getHealingRenderDataId(healing)

				// Mark as pending to prevent duplicate loading
				this.pendingAiSources.add(cacheId)
				try {
					const data = await this.decodeAiSource(aiSource, {
						applyBrushMask: healing.applyBrushMask ?? true,
						applyAlignment: healing.applyAlignment ?? true,
						destinationRect: healing.destinationRect,
					})
					if (data) {
						this.setAiSource(cacheId, data) // Store with cache ID (includes variation + settings)
					}
				} catch (error) {
					console.error("Failed to hydrate AI healing source", error)
				} finally {
					this.pendingAiSources.delete(cacheId)
				}
			})

		await Promise.all(tasks)
		if (tasks.length > 0) {
			this.needCompute = true
		}
	}

	/**
	 * Decode an AI-generated source from base64 into a cropped, aligned ImageBitmap.
	 *
	 * Steps: base64 → full bitmap → crop to brush region (or full rect) → apply
	 * optical-flow alignment by shifting/scaling the bitmap content (the destination
	 * rect and mask stay unchanged — this is distinct from the user moving the rect).
	 */
	private async decodeAiSource(
		source: HealingGeneratedSource,
		options: { applyBrushMask: boolean; applyAlignment: boolean; destinationRect: Rectangle },
	): Promise<AiSourceData | null> {
		// 1. Decode full bitmap from base64
		const byteArray = base64ToUint8Array(source.data)
		const blob = new Blob([byteArray], { type: source.mimeType })
		const fullBitmap = await createImageBitmap(blob)

		// 2. Determine crop region based on applyBrushMask
		let cropX: number, cropY: number, cropW: number, cropH: number

		const { applyBrushMask, applyAlignment } = options
		if (applyBrushMask) {
			// Crop to the brushed region
			cropX = source.cropRect.x * fullBitmap.width
			cropY = source.cropRect.y * fullBitmap.height
			cropW = source.cropRect.width * fullBitmap.width
			cropH = source.cropRect.height * fullBitmap.height
		} else {
			// Use full rectangle region
			cropX = 0
			cropY = 0
			cropW = fullBitmap.width
			cropH = fullBitmap.height
		}

		// 3. Calculate alignment (translation + small isotropic scale)
		let offsetX = 0
		let offsetY = 0
		let scaleX = 1
		let scaleY = 1
		if (applyAlignment && source.alignment) {
			const alignment = convertAlignmentToUnoriented(
				source.alignment,
				source.orientation,
				fullBitmap.width,
				fullBitmap.height,
			)

			// Clamp alignment to prevent referencing pixels outside image bounds at edges
			const clamped = clampAlignmentAtEdges({
				offsetX: alignment.dx,
				offsetY: alignment.dy,
				scaleX: alignment.scaleX ?? 1,
				scaleY: alignment.scaleY ?? 1,
				destinationRect: options.destinationRect,
				cropRect: source.cropRect,
				fullBitmapWidth: fullBitmap.width,
				fullBitmapHeight: fullBitmap.height,
			})
			offsetX = clamped.offsetX
			offsetY = clamped.offsetY
			scaleX = clamped.scaleX
			scaleY = clamped.scaleY
		}

		// 4. Crop + align using canvas
		const canvas = new OffscreenCanvas(Math.round(cropW), Math.round(cropH))
		const ctx = canvas.getContext("2d")
		if (!ctx) {
			fullBitmap.close()
			return null
		}

		// Apply scale by sampling a scaled source region around the crop center
		const srcW = cropW / scaleX
		const srcH = cropH / scaleY
		const srcX = cropX - offsetX + (cropW - srcW) / 2
		const srcY = cropY - offsetY + (cropH - srcH) / 2

		ctx.drawImage(fullBitmap, srcX, srcY, srcW, srcH, 0, 0, cropW, cropH)

		const processedBitmap = await createImageBitmap(canvas)

		// Save dimensions before closing bitmap
		const fullWidth = fullBitmap.width
		const fullHeight = fullBitmap.height
		fullBitmap.close()

		console.log("AI source decoded", {
			fullSize: `${fullWidth}x${fullHeight}`,
			cropRegion: `${cropX.toFixed(0)},${cropY.toFixed(0)} ${cropW.toFixed(
				0,
			)}x${cropH.toFixed(0)}`,
			alignment: applyAlignment
				? `sX=${scaleX.toFixed(4)}, sY=${scaleY.toFixed(4)}, ${offsetX.toFixed(
						1,
					)},${offsetY.toFixed(1)} (conf: ${source.alignment?.confidence.toFixed(2)})`
				: "disabled",
			finalSize: `${processedBitmap.width}x${processedBitmap.height}`,
		})

		return {
			bitmap: processedBitmap,
			width: processedBitmap.width,
			height: processedBitmap.height,
		}
	}

	private copyFrom(options: { source: Texture; srcLocation: Rectangle; destination: Texture }) {
		copyFrom({ ...options, renderer: this.renderer })
	}

	private copyTo(options: {
		source: Texture
		destination: Texture
		mask: Texture
		destLocation: Rectangle
	}) {
		copyTo({ ...options, renderer: this.renderer })
	}

	private copyToWithImageTransform(options: {
		source: Texture
		mask: Texture
		destination: Texture
		destLocation: Rectangle
		scissorBounds: ScissorBounds | null
		transform: HealingCompositeTransform
	}) {
		const { source, mask, destination, destLocation, scissorBounds, transform } = options
		const { renderer } = this
		const { gl, shaders } = renderer.backend

		renderer.setRenderTarget(destination, [renderer.alphaTexture])

		gl.enable(gl.BLEND)
		gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)

		source.bind(0)
		mask.bind(1)

		shaders.healingTransformCombine.setUniforms({
			result: 0,
			mask: 1,
			destinationRect: [
				destLocation.x,
				destLocation.y,
				destLocation.width,
				destLocation.height,
			],
			cropMatrix: renderer.cropMatrix,
			perspectiveMatrix: renderer.perspectiveMatrix,
			...transform.lensUniforms,
			...transform.chromaticAberrationUniforms,
		})

		if (scissorBounds) {
			gl.enable(gl.SCISSOR_TEST)
			gl.scissor(scissorBounds.x, scissorBounds.y, scissorBounds.width, scissorBounds.height)
		}

		try {
			shaders.healingTransformCombine.draw()
		} finally {
			if (scissorBounds) gl.disable(gl.SCISSOR_TEST)
			gl.disable(gl.BLEND)
		}
	}

	private createCompositeTransform(outputSize: vec2): HealingCompositeTransform {
		const { renderer } = this
		const { photo, profileManager } = renderer
		const lensUniforms = profileManager.getLensUniforms(photo)
		const chromaticAberrationUniforms = profileManager.getChromaticAberrationUniforms(photo)
		const boundsContext = createHealingTransformBoundsContext({
			cropMatrix: renderer.cropMatrix,
			perspectiveMatrix: renderer.perspectiveMatrix,
			outputSize,
			...lensUniforms,
			renderChromaticAberrationCorrection:
				chromaticAberrationUniforms.renderChromaticAberrationCorrection,
			chromaticAberrationParams: profileManager.lensChromaticAberrationParams,
		})

		return { boundsContext, lensUniforms, chromaticAberrationUniforms }
	}

	private invalidate(
		healingData: HealingRenderData | undefined,
		options: {
			invalidateResult?: boolean
			invalidateMask: boolean
		},
	): boolean {
		const { invalidateMask } = options
		const invalidateResult = options.invalidateResult ?? true
		let resultInvalidated = false

		if (healingData) {
			if (invalidateResult && healingData.resultPatch) {
				this.texturesPool.deposit(healingData.resultPatch)
				healingData.resultPatch = null
				resultInvalidated = true
			}

			if (invalidateMask && healingData.mask) {
				this.masksPool.deposit(healingData.mask)
				healingData.mask = null
			}
		}

		return resultInvalidated
	}

	private release(healingData: HealingRenderData | undefined) {
		if (healingData) {
			this.texturesPool.deposit(healingData.resultPatch)
			healingData.resultPatch = null
			this.masksPool.deposit(healingData.mask)
			healingData.mask = null
		}
	}

	private rectUpdated(renderData: HealingRenderData, item: HealingItem) {
		return (
			!rectEquals(renderData.destination, item.destinationRect) ||
			!rectEquals(renderData.source, item.sourceRect)
		)
	}

	private featherUpdated(renderData: HealingRenderData, item: HealingItem) {
		return !equals(renderData.strokes[0]?.feather, item.strokes[0]?.feather)
	}

	private opacityUpdated(renderData: HealingRenderData, item: HealingItem) {
		return !equals(renderData.strokes[0]?.density, item.strokes[0]?.density)
	}

	private updateHealingData(
		reusedData: HealingRenderData,
		item: HealingItem,
		textureChanged: boolean,
	) {
		const selectedAiSource = this.getSelectedAiSourceFromItem(item)
		if (item.applyBrushMask === false && selectedAiSource?.fullRect) {
			item = {
				...item,
				destinationRect: selectedAiSource.fullRect,
				sourceRect: selectedAiSource.fullRect,
			}
		}
		const rectUpdated = this.rectUpdated(reusedData, item)
		const rectSizeUpdated = !rectSizeEquals(reusedData.destination, item.destinationRect)

		const featherUpdated = this.featherUpdated(reusedData, item)
		const opacityUpdated = this.opacityUpdated(reusedData, item)

		const aiSourceChanged =
			reusedData.aiSource?.data.slice(0, 100) !== selectedAiSource?.data.slice(0, 100) ||
			reusedData.aiSource?.mimeType !== selectedAiSource?.mimeType ||
			reusedData.aiSource?.width !== selectedAiSource?.width ||
			reusedData.aiSource?.height !== selectedAiSource?.height

		const brushMaskChanged = reusedData.applyBrushMask !== item.applyBrushMask

		const newAlignment = item.aiVariations?.[item.selectedVariation ?? 0]?.alignment
		const alignmentChanged =
			reusedData.applyAlignment !== item.applyAlignment ||
			reusedData.alignment?.dx !== newAlignment?.dx ||
			reusedData.alignment?.dy !== newAlignment?.dy

		// mask doesn't change when moving dest/src, unless dest/src size changes (moves out/in of image bounds)
		const invalidateMask =
			featherUpdated || opacityUpdated || rectSizeUpdated || brushMaskChanged

		const invalidateResult =
			rectUpdated || textureChanged || aiSourceChanged || brushMaskChanged || alignmentChanged

		this.invalidate(reusedData, { invalidateResult, invalidateMask })

		// update strokes
		if (rectUpdated || featherUpdated) {
			reusedData.strokes = this.getAdjustedStrokesByFeather(item.strokes)
		} else if (opacityUpdated) {
			const [oldStroke, newStroke] = [reusedData.strokes[0], item.strokes[0]]
			if (oldStroke && newStroke) {
				oldStroke.density = newStroke.density
			}
		}

		reusedData.destination = { ...item.destinationRect }
		reusedData.source = { ...item.sourceRect }
		reusedData.aiSource = selectedAiSource
		reusedData.applyBrushMask = item.applyBrushMask // Update brush mask setting
		reusedData.applyAlignment = item.applyAlignment // Update alignment setting
		reusedData.alignment = newAlignment // Update alignment setting

		if (invalidateResult) {
			this.needCompute = true
		}

		return reusedData
	}

	getAdjustedStrokesByFeather(strokes: BrushStroke[]) {
		const boundaries: vec2 = [
			this.renderer.photo.texture.width,
			this.renderer.photo.texture.height,
		]

		return strokes.map((stroke) => adjustBrushSpacingByFeather(stroke, boundaries))
	}

	/**
	 * Cascade invalidation: if a patch's source region overlaps a destination that
	 * is being recomputed, its cached result is stale and must also be recomputed.
	 */
	invalidateResultIfSourceToBeUpdated(healings: HealingRenderData[]): boolean {
		const updatedRegions: Rectangle[] = []
		let resultInvalidated = false

		for (const healing of healings) {
			if (!healing.resultPatch || !healing.mask) {
				updatedRegions.push(healing.destination)
			} else {
				for (const updatedRect of updatedRegions) {
					if (rectOverlap(healing.source, updatedRect)) {
						const didInvalidateResult = this.invalidate(healing, {
							invalidateMask: false,
						})
						resultInvalidated = didInvalidateResult || resultInvalidated
						updatedRegions.push(healing.destination)
						break
					}
				}
			}
		}

		return resultInvalidated
	}

	/**
	 * Sync internal render data with the current list of healing items.
	 * Detects rect/feather/opacity/AI-source/alignment changes, invalidates
	 * stale caches, and returns whether any patch needs (re)computation.
	 */
	update(items: HealingItem[], textureChanged: boolean): boolean {
		if (items.length < this.healings.length) {
			this.texturesPool.reduceCapacity()
			this.masksPool.reduceCapacity()
		}

		const newHealings: HealingRenderData[] = []
		for (const item of items) {
			// Generate cache ID that includes variation + settings (alignment, brush mask)
			const cacheId = getHealingRenderDataId(item)

			const index = this.healings.findIndex((value) => value.id === cacheId)
			const reusedData = this.healings[index]
			if (reusedData) {
				// existing healing item
				const updatedData = this.updateHealingData(reusedData, item, textureChanged)

				newHealings.push(updatedData)
				this.healings.splice(index, 1)
			} else {
				// new healing item
				this.needCompute = true
				const selectedAiSource = this.getSelectedAiSourceFromItem(item)

				let destination = { ...item.destinationRect }
				let source = { ...item.sourceRect }
				if (item.applyBrushMask === false && selectedAiSource?.fullRect) {
					destination = { ...selectedAiSource.fullRect }
					source = { ...selectedAiSource.fullRect }
				}
				newHealings.push({
					id: cacheId,
					strokes: this.getAdjustedStrokesByFeather(item.strokes),
					resultPatch: null,
					mask: null,
					destination: destination,
					source: source,
					aiSource: selectedAiSource,
					applyBrushMask: item.applyBrushMask, // Copy the brush mask setting
					applyAlignment: item.applyAlignment, // Copy the alignment setting
					alignment: item.aiVariations?.[item.selectedVariation ?? 0]?.alignment, // Copy the alignment setting
				})
			}
		}

		// Deposit textures back to pool for deleted healing patches:
		for (const item of this.healings) {
			this.release(item)
		}

		// Cascaded result invalidation must force compute; fastRender skips null resultPatch.
		if (this.invalidateResultIfSourceToBeUpdated(newHealings)) {
			this.needCompute = true
		}

		this.healings = newHealings

		return this.needCompute
	}

	private getDimension(rect: Rectangle): vec2 {
		return getRectDimension(rect, this.width, this.height)
	}

	private createMask(healingData: HealingRenderData): Texture {
		const { destination: rect, strokes, applyBrushMask } = healingData
		const [width, height] = this.getDimension(rect) // scaled resolution

		if (applyBrushMask !== false) {
			const texture = this.masksPool.withdraw(width, height, "healingRenderer.maskTexture")

			// Use actual brush strokes as mask
			this.maskPainter.drawMask(strokes, texture, {
				isAdditive: false,
				rect: rect,
				aspectRatio: this.height / this.width,
			})

			return texture
		} else {
			// Create a full rectangle mask (minus edge pixels), for full resolution only
			return this.createRectangleMask(healingData)
		}
	}

	/**
	 * Create a full-rectangle mask with feathered edges for AI fill mode (applyBrushMask=false).
	 * Edge padding accounts for alignment shift/scale so the blended border
	 * doesn't sample outside the AI bitmap (would be black pixels). Edges touching the image
	 * boundary get zero padding (handled by Poisson blending).
	 */
	private createRectangleMask(healingData: HealingRenderData): Texture {
		const renderer = this.renderer
		const rect = healingData.destination
		const alignmentOriented = healingData.applyAlignment ? healingData.alignment : undefined
		const orientation = healingData.aiSource?.orientation
		const stroke = healingData.strokes[0]

		const { nearLeftEdge, nearRightEdge, nearTopEdge, nearBottomEdge } = isNearEdge(rect)

		const uncroppedSize = renderer.photo.imageState.originalImageSize
		const [width, height] = getRectDimension(rect, uncroppedSize[0], uncroppedSize[1]) // this.getDimension(rect)
		const texture = this.masksPool.withdraw(width, height, "healingRenderer.maskTexture")

		const alignment = alignmentOriented
			? convertAlignmentToUnoriented(alignmentOriented, orientation ?? 0, width, height)
			: undefined

		// Create a white rectangle that fills the texture, minus edge pixels
		// Use different edge sizes based on alignment shift and scale
		const baseEdge = 15
		const dx = Math.round(alignment?.dx ?? 0)
		const dy = Math.round(alignment?.dy ?? 0)
		const sX = Math.abs(1 / (alignment?.scaleX ?? 1) - 1) / 2
		const sY = Math.abs(1 / (alignment?.scaleY ?? 1) - 1) / 2

		// srcX = cropX - offsetX + (cropW - srcW) / 2
		const scalePadX = Math.round(width * sX)
		const scalePadY = Math.round(height * sY)

		const leftEdge = nearLeftEdge ? 0 : Math.max(baseEdge, dx + baseEdge + scalePadX)
		const rightEdge = nearRightEdge ? 0 : Math.max(baseEdge, -dx + baseEdge + scalePadX)
		const topEdge = nearTopEdge ? 0 : Math.max(baseEdge, dy + baseEdge + scalePadY)
		const bottomEdge = nearBottomEdge ? 0 : Math.max(baseEdge, -dy + baseEdge + scalePadY)

		console.log("createRectangleMask", {
			alignment,
			scalePadX,
			scalePadY,
			leftEdge,
			rightEdge,
			topEdge,
			bottomEdge,
			width,
			height,
		})

		// Set the render target to the mask texture
		renderer.setRenderTarget(texture)

		// Clear the texture to black (transparent)
		const { backend } = renderer
		const gl = backend.gl
		gl.clearColor(0, 0, 0, 0)
		gl.clear(gl.COLOR_BUFFER_BIT)

		const innerX = leftEdge
		const innerY = topEdge
		const innerWidth = Math.max(0, width - leftEdge - rightEdge)
		const innerHeight = Math.max(0, height - topEdge - bottomEdge)

		if (innerWidth > 0 && innerHeight > 0) {
			// Determine opacity (density) and feather in pixels
			const density = clamp(stroke?.density ?? 1, 0, 1)
			const featherFrac = clamp(stroke?.feather ?? 0, 0, 1)

			gl.enable(gl.SCISSOR_TEST)

			if (featherFrac <= 0) {
				// Solid rectangle with specified opacity
				gl.clearColor(density, density, density, density)
				gl.scissor(innerX, innerY, innerWidth, innerHeight)
				gl.clear(gl.COLOR_BUFFER_BIT)
				gl.disable(gl.SCISSOR_TEST)
			} else {
				// Compute per-axis feather in pixels
				const maxFeatherX = Math.round(0.3 * innerWidth)
				const maxFeatherY = Math.round(0.3 * innerHeight)
				let featherPxX = Math.round(featherFrac * maxFeatherX)
				let featherPxY = Math.round(featherFrac * maxFeatherY)
				featherPxX = Math.min(featherPxX, Math.floor(innerWidth / 2))
				featherPxY = Math.min(featherPxY, Math.floor(innerHeight / 2))

				// Stepwise inward fills to approximate feathered edge (outer low alpha → inner high alpha)
				const steps = Math.min(
					64,
					Math.max(4, Math.max(Math.round(featherPxX), Math.round(featherPxY))),
				)
				for (let s = 0; s < steps; s++) {
					const t = (s + 1) / steps // 0→1 inward
					// Smoothstep for a smoother gradient
					const smooth = t // * t * (3 - 2 * t)
					const alpha = density * smooth
					// Start from full inner rect and shrink inward per-axis
					const shrinkX = Math.round((featherPxX * s) / steps)
					const shrinkY = Math.round((featherPxY * s) / steps)
					const x = innerX + shrinkX
					const y = innerY + shrinkY
					const w = Math.max(0, innerWidth - 2 * shrinkX)
					const h = Math.max(0, innerHeight - 2 * shrinkY)
					if (w <= 0 || h <= 0) break
					gl.clearColor(alpha, alpha, alpha, alpha)
					gl.scissor(x, y, w, h)
					gl.clear(gl.COLOR_BUFFER_BIT)
				}
				// Ensure center reaches full density
				const cx = innerX + featherPxX
				const cy = innerY + featherPxY
				const cw = Math.max(0, innerWidth - 2 * featherPxX)
				const ch = Math.max(0, innerHeight - 2 * featherPxY)
				if (cw > 0 && ch > 0) {
					gl.clearColor(density, density, density, density)
					gl.scissor(cx, cy, cw, ch)
					gl.clear(gl.COLOR_BUFFER_BIT)
				}
				gl.disable(gl.SCISSOR_TEST)
			}
		}

		return texture
	}

	/**
	 * Get the mask for the current resolution.
	 *  compute: mask is always at full resolution of original image
	 *  no-compute, but mask changes (e.g. feather/opacity change): mask is re-created at render-resolution (for brush only, not rectangle in ai fill),
	 * which could be smaller than full-resolution. So need to check and increase resolution when needed.
	 */
	private getMaskForCurrentResolution(healingData: HealingRenderData): Texture {
		const rect = healingData.destination
		const mask = healingData.mask
		// Check if we should apply brush mask from the healing data's applyBrushMask field (default true)
		const applyBrushMask = healingData.applyBrushMask ?? true

		if (!mask) {
			healingData.mask = this.createMask(healingData)
			return healingData.mask
		}

		// If current mask is smaller than desired resolution, re-draw it.
		const [width, height] = this.getDimension(rect)
		if (mask.width < width || mask.height < height) {
			if (applyBrushMask) {
				mask.updateSize(width, height)

				this.maskPainter.drawMask(healingData.strokes, mask, {
					isAdditive: false,
					rect: rect,
					aspectRatio: this.height / this.width,
				})
				return mask
			} else {
				this.masksPool.deposit(mask)
				healingData.mask = this.createRectangleMask(healingData)
				return healingData.mask
			}
		}

		return mask
	}

	private poissonIteration(options: { pixels: [Texture, Texture]; maxIter: number; w: number }) {
		const { pixels, maxIter, w } = options
		const a = 4.0

		let i = 0
		for (let iter = 0; iter < maxIter * 2; ++iter) {
			i = (iter + 1) % 2
			this.renderer.setRenderTarget(pixels[i])
			pixels[1 - i]?.bind(0)

			this.renderer.shaders.healingPoission.render({
				pixels: 0,
				a: a,
				w: w,
				parity: iter % 2,
			})
		}
	}

	/**
	 * Poisson image editing (seamless cloning).
	 * Solves ∇²f = ∇²(target−source) under boundary conditions from the mask,
	 * then adds the result back to source. Uses a downscaled red-black SOR
	 * solver for speed and smoother result, then upsamples to full resolution.
	 *
	 * Ref: https://gitlab.gnome.org/GNOME/gimp/-/blob/master/app/paint/gimpheal.c
	 */
	private poissonImaging(
		source: Texture,
		target: Texture,
		mask: Texture,
		edgeFlags: number[],
		isAiFill: boolean,
	) {
		const renderer = this.renderer
		const shaders = renderer.shaders

		// downscale to do poisson for faster speed and smoother result
		const scale = clamp(100 / Math.max(target.width, target.height), 0.1, 1)
		const [width, height] = [
			Math.round(target.width * scale),
			Math.round(target.height * scale),
		]

		const pixels: [Texture, Texture] = [
			this.texturesPool.withdraw(width, height, "healingRenderer.poissonImagingTexture.0"),
			this.texturesPool.withdraw(width, height, "healingRenderer.poissonImagingTexture.1"),
		]

		// If rect is at the image edges, and mask covers the edges, find the true mask boundary points coordinates along the edges, so we can interpolate the edge pixels correctly in shader
		// Example: imagine you are healing a black hole on a white wall at the image edge, you need to use white wall color as the boundary condition for poisson blending.
		// edgeFlags: left, top, right, bottom
		const endpoints = this.getEdgeEndPoints(mask, edgeFlags)

		// pixels = destination - source; downsample/process mask and store in pixels.a
		renderer.setRenderTarget(pixels[0])
		source.bind(0)
		target.bind(1)
		mask.bind(2)
		shaders.makeDiff.render({
			outputSize: [width, height],
			source: 0,
			destination: 1,
			mask: 2,
			edgeFlags: edgeFlags,
			leftBounds: endpoints.left,
			rightBounds: endpoints.right,
			topBounds: endpoints.top,
			bottomBounds: endpoints.bottom,
			isAiFill: isAiFill,
		})

		// Main iterations on downsampled dimension
		// Note: w > 0.25 only works with red-black checkerboard update order (use parity)
		const count = (width * height) / 2 // approximate mask count
		const w = (2.0 - 1.0 / (0.1575 * Math.sqrt(count) + 0.8)) * 0.25
		const maxIter = clamp(Math.round((width * height) / 70), 50, 500)
		this.poissonIteration({ pixels, maxIter, w })

		// result = source + pixels
		pixels[1].updateSize(target.width, target.height)
		renderer.setRenderTarget(pixels[1])
		pixels[0].bind(0)
		source.bind(1)
		shaders.healingAdd.render({
			pixels: 0,
			source: 1,
		})

		this.texturesPool.deposit(pixels[0])

		return pixels[1]
	}

	private getEdgeEndPoints(mask: Texture, edgeFlags: number[]) {
		// Use normalized coordinates [0-1] for endpoints
		const endpoints = {
			left: [-1, -1], // [y1, y2] on the left edge
			right: [-1, -1], // [y1, y2] on the right edge
			top: [-1, -1], // [x1, x2] on the top edge
			bottom: [-1, -1], // [x1, x2] on the bottom edge
		}
		if (!(edgeFlags[0] || edgeFlags[1] || edgeFlags[2] || edgeFlags[3])) {
			return endpoints
		}

		const maskData = new Uint8Array(mask.width * mask.height * 4)
		mask.read({ bufferView: maskData })

		const getMaskValue = (x: number, y: number) => {
			return maskData[(y * mask.width + x) * 4] ?? 0
		}

		if (edgeFlags[0]) {
			// Left edge
			let minY = -1,
				maxY = -1
			for (let y = 0; y < mask.height; y++) {
				if (getMaskValue(0, y) > 0) {
					if (minY === -1) minY = y
					maxY = y
				}
			}
			if (minY !== -1) {
				endpoints.left[0] = minY / (mask.height - 1) // y1
				endpoints.left[1] = maxY / (mask.height - 1) // y2
			} else {
				edgeFlags[0] = 0
			}
		}

		if (edgeFlags[1]) {
			// Top edge
			let minX = -1,
				maxX = -1
			for (let x = 0; x < mask.width; x++) {
				if (getMaskValue(x, 0) > 0) {
					if (minX === -1) minX = x
					maxX = x
				}
			}
			if (minX !== -1) {
				endpoints.top[0] = minX / (mask.width - 1) // x1
				endpoints.top[1] = maxX / (mask.width - 1) // x2
			} else {
				edgeFlags[1] = 0
			}
		}

		if (edgeFlags[2]) {
			// Right edge
			let minY = -1,
				maxY = -1
			for (let y = 0; y < mask.height; y++) {
				if (getMaskValue(mask.width - 1, y) > 0) {
					if (minY === -1) minY = y
					maxY = y
				}
			}
			if (minY !== -1) {
				endpoints.right[0] = minY / (mask.height - 1) // y1
				endpoints.right[1] = maxY / (mask.height - 1) // y2
			} else {
				edgeFlags[2] = 0
			}
		}

		if (edgeFlags[3]) {
			// Bottom edge
			let minX = -1,
				maxX = -1
			for (let x = 0; x < mask.width; x++) {
				if (getMaskValue(x, mask.height - 1) > 0) {
					if (minX === -1) minX = x
					maxX = x
				}
			}
			if (minX !== -1) {
				endpoints.bottom[0] = minX / (mask.width - 1) // x1
				endpoints.bottom[1] = maxX / (mask.width - 1) // x2
			} else {
				edgeFlags[3] = 0
			}
		}

		return endpoints
	}

	/**
	 * Compute (if needed) and composite a single healing patch onto the read/write buffers.
	 * For AI sources: loads the decoded bitmap into a texture and converts sRGB → ProPhoto.
	 * For manual clones: copies the source region directly from the current composited image.
	 * Then runs Poisson blending and alpha-composites the result at the destination rect.
	 */
	private drawHealing = (healingItem: HealingRenderData, rwBuffers: DualBuffers): void => {
		const destinationRect = healingItem.destination

		const [width, height] = this.getDimension(destinationRect)
		if (width <= 2 || height <= 2) {
			console.warn("healing patch too small (out-of-bound)", { healingItem })
			return
		}

		if (healingItem.aiSource && !this.aiSources.has(healingItem.id)) {
			void this.ensureAiSourceLoaded(healingItem)
			// Skip rendering this healing until AI source is loaded
			console.warn("!!!AI healing source not decoded yet", { healingItem })
			return
		}

		if (!healingItem.resultPatch) {
			// mask
			const mask = this.getMaskForCurrentResolution(healingItem)

			// destination
			const target = this.texturesPool.withdraw(
				width,
				height,
				"healingRenderer.targetTexture",
			)
			this.copyFrom({
				source: rwBuffers.readBuffer,
				srcLocation: destinationRect,
				destination: target,
			})

			// source
			const [srcWidth, srcHeight] = this.getDimension(healingItem.source)
			const sourceTex = this.texturesPool.withdraw(
				srcWidth,
				srcHeight,
				"healingRenderer.sourceTexture",
			)
			if (healingItem.aiSource) {
				const aiData = this.aiSources.get(healingItem.id)
				if (!aiData) {
					this.texturesPool.deposit(sourceTex)
					this.texturesPool.deposit(target)
					console.warn("AI healing source missing", { id: healingItem.id })
					return
				}

				// Load bitmap to texture and convert to prophoto with inverse tonemap and exposure
				const tempTex = this.masksPool.withdraw(
					srcWidth,
					srcHeight,
					"healingRenderer.tempTexture",
				)
				tempTex.update({
					width: srcWidth,
					height: srcHeight,
					format: "rgba8unorm",
					data: aiData.bitmap,
				})
				this.renderSrgbToProPhotoForAi(
					tempTex,
					sourceTex,
					healingItem.aiSource.aiInputNormalization,
				)
				this.masksPool.deposit(tempTex)
			} else {
				this.copyFrom({
					source: rwBuffers.readBuffer,
					srcLocation: healingItem.source,
					destination: sourceTex,
				})
			}

			// run poisson imaging
			const edgeFlags = [0, 0, 0, 0]
			edgeFlags[0] = destinationRect.x === 0 ? 1 : 0
			edgeFlags[1] = destinationRect.y === 0 ? 1 : 0
			edgeFlags[2] = destinationRect.x + destinationRect.width === 1 ? 1 : 0
			edgeFlags[3] = destinationRect.y + destinationRect.height === 1 ? 1 : 0
			healingItem.resultPatch = this.poissonImaging(
				sourceTex,
				target,
				mask,
				edgeFlags,
				healingItem.aiSource ? true : false,
			)
			console.log("poisson imaging run finished for feather", healingItem.strokes[0]?.feather)
			this.texturesPool.deposit(sourceTex)
			this.texturesPool.deposit(target)
		}

		if (healingItem.resultPatch) {
			const mask = this.getMaskForCurrentResolution(healingItem)

			// draw to output with alpha blending
			rwBuffers.swapBuffers()
			this.copyTo({
				source: healingItem.resultPatch,
				mask,
				destination: rwBuffers.writeBuffer,
				destLocation: destinationRect,
			})
			rwBuffers.swapBuffers()
		}

		return
	}

	/** Convert an sRGB AI bitmap to ProPhoto RGB with inverse tonemap + exposure undo. */
	renderSrgbToProPhotoForAi(
		source: Texture,
		destination: Texture,
		aiInputNormalization?: HealingAiInputNormalization,
	) {
		const { renderer } = this
		const shaders = renderer.shaders
		const photo = renderer.photo

		destination.updateSize(source.width, source.height)

		renderer.setRenderTarget(destination)
		source.bind(0)

		const effectiveExposure =
			aiInputNormalization?.effectiveExposure ??
			(photo.adjustments.exposure ?? 0) + (photo.adjustments.shadows ?? 0)
		shaders.healingAiToProphoto.render({
			source: 0,
			exposure: effectiveExposure,
		})
	}

	/**
	 * Capture the current image (with all prior healing patches composited) as
	 * sRGB input for the AI inpainting model.
	 *
	 * Pipeline: stable healing geometry → composite previous healings → apply
	 * effective exposure (exposure + shadows) and convert ProPhoto → sRGB.
	 * Effective exposure is baked in so RAW photos aren't too dark/bright for the model.
	 */
	renderAiInput(
		rect: Rectangle,
		outputSize: vec2,
		aiInputNormalization?: HealingAiInputNormalization,
	) {
		const renderer = this.renderer
		const rwBuffers = renderer.dualBuffers
		const { shaders, photo } = renderer

		const [uncroppedW, uncroppedH] = photo.imageState.originalImageSize

		// Render stable healing space: no crop, perspective, or geometric lens correction.
		rwBuffers.updateSize(uncroppedW, uncroppedH)
		renderer.transformPass.render(photo.texture, rwBuffers.readBuffer, { isForHealing: true })

		this.width = uncroppedW
		this.height = uncroppedH

		// compose previous healed patches to the full readBuffer (in uncropped space)
		for (const item of this.healings) {
			this.drawHealing(item, rwBuffers)
		}

		const effectiveExposure =
			aiInputNormalization?.effectiveExposure ??
			(photo.adjustments.exposure ?? 0) + (photo.adjustments.shadows ?? 0)

		// Convert the composited image to sRGB with effective exposure for the AI inpainting model
		renderer.setRenderTarget(rwBuffers.writeBuffer)
		rwBuffers.readBuffer.bind(0)
		shaders.healingAiToSrgb.render({
			source: 0,
			exposure: effectiveExposure,
		})
		rwBuffers.swapBuffers()

		// Copy the sRGB texture of the target rect region and read as Uint8Array
		const captureTexture = this.masksPool.withdraw(
			outputSize[0],
			outputSize[1],
			"healing.ai.capture",
		)

		copyFrom({
			renderer,
			source: rwBuffers.readBuffer,
			srcLocation: rect,
			destination: captureTexture,
		})
		const pixels = captureTexture.read() as Uint8Array
		this.masksPool.deposit(captureTexture)

		return pixels
	}

	/**
	 * Full pipeline: render stable healing space → compute/cache all healing
	 * patches (Poisson blend at full resolution) → render the visible frame
	 * normally and composite cached patches. Keeping visible rendering on the
	 * normal transform path avoids resampling the full photo through the healing
	 * compute buffer.
	 * Called when any patch needs (re)computation (`needCompute = true`).
	 */
	computeAndRender(rwBuffers: DualBuffers, outputSize: vec2) {
		const renderer = this.renderer
		const photo = renderer.photo

		// If need computation: compute healed resultPatch from full resolution
		let [uncroppedW, uncroppedH] = photo.imageState.originalImageSize

		// If no computation: transform to downscaled size
		// Note: this condition won't happen because we now use fastRender when this.needCompute=false
		if (!this.needCompute) {
			const scale = outputSize[0] / photo.imageState.croppedImageSize[0]
			uncroppedW = Math.round(uncroppedW * scale)
			uncroppedH = Math.round(uncroppedH * scale)
		}

		// Render stable healing space: no crop, perspective, or geometric lens correction.
		rwBuffers.updateSize(uncroppedW, uncroppedH)
		renderer.transformPass.render(photo.texture, rwBuffers.readBuffer, { isForHealing: true })

		this.width = uncroppedW
		this.height = uncroppedH

		// Compute healed patch and compose to the full readBuffer (in uncropped space)
		this.renderer.performanceCounters.begin("healingPassComputeAndRender")
		for (const item of this.healings) {
			this.drawHealing(item, rwBuffers)
		}
		this.renderer.performanceCounters.end("healingPassComputeAndRender")

		this.needCompute = false

		// Render the visible frame through the same normal transform + fast composite
		// path used by subsequent/manual rerenders. The full-resolution healing buffer
		// is only for computing patches, not for final display.
		rwBuffers.updateSize(outputSize[0], outputSize[1])
		renderer.transformPass.render(photo.texture, rwBuffers.writeBuffer)
		rwBuffers.swapBuffers()
		this.fastRender(rwBuffers, outputSize)
	}

	/** Composite a pre-computed healing patch using the crop transform (no Poisson recompute). */
	private fastDrawHealing(
		healingItem: HealingRenderData,
		rwBuffers: DualBuffers,
		transform: HealingCompositeTransform,
	) {
		const resultPatch = healingItem.resultPatch
		if (resultPatch) {
			const destinationRect = healingItem.destination
			const [width, height] = this.getDimension(destinationRect)
			if (width <= 2 || height <= 2) {
				console.warn("healing patch too small (out-of-bound)", { healingItem })
				return
			}

			const mask = this.getMaskForCurrentResolution(healingItem)
			const boundsKey = `${transform.boundsContext.cacheKey},${destinationRect.x},${destinationRect.y},${destinationRect.width},${destinationRect.height}`
			if (healingItem.transformBoundsCache?.key !== boundsKey) {
				healingItem.transformBoundsCache = {
					key: boundsKey,
					bounds: getHealingTransformScissorBounds(
						destinationRect,
						transform.boundsContext,
					),
				}
			}
			const scissorBounds = healingItem.transformBoundsCache.bounds
			if (scissorBounds && (scissorBounds.width === 0 || scissorBounds.height === 0)) return

			// draw to output with alpha blending
			rwBuffers.swapBuffers()
			this.copyToWithImageTransform({
				source: resultPatch,
				mask,
				destination: rwBuffers.writeBuffer,
				destLocation: destinationRect,
				scissorBounds,
				transform,
			})
			rwBuffers.swapBuffers()
		}
	}

	/**
	 * Fast path: reuse already-computed result patches and composite them at render
	 * resolution via crop transform. Used when no patch needs recomputation
	 * (e.g. viewport resize, crop adjustment, parameter changes that don't affect healing).
	 */
	fastRender(rwBuffers: DualBuffers, outputSize: vec2) {
		if (this.healings.length === 0) return

		const photo = this.renderer.photo
		let [uncroppedW, uncroppedH] = photo.imageState.originalImageSize

		const scale = outputSize[0] / photo.imageState.croppedImageSize[0]
		uncroppedW = Math.round(uncroppedW * scale)
		uncroppedH = Math.round(uncroppedH * scale)

		this.width = uncroppedW
		this.height = uncroppedH
		const transform = this.createCompositeTransform(outputSize)

		for (const item of this.healings) {
			this.fastDrawHealing(item, rwBuffers, transform)
		}
	}

	dispose() {
		this.texturesPool.dispose()
		this.masksPool.dispose()
		this.healings = []
		//this.removeAllAiSources()
	}

	private ensureAiSourceLoaded(healingData: HealingRenderData) {
		const { id, aiSource } = healingData
		if (!aiSource || this.pendingAiSources.has(id)) {
			return
		}
		this.pendingAiSources.add(id)
		void this.decodeAiSource(aiSource, {
			applyBrushMask: healingData.applyBrushMask ?? true,
			applyAlignment: healingData.applyAlignment ?? true,
			destinationRect: healingData.destination,
		})
			.then((data) => {
				if (data) {
					this.setAiSource(id, data)
					this.needCompute = true
				}
			})
			.catch((error) => {
				console.error("Failed to decode AI healing source", { id, error })
			})
			.finally(() => {
				this.pendingAiSources.delete(id)
			})
	}
}

const base64ToUint8Array = (data: string) => {
	const binaryString = atob(data)
	const len = binaryString.length
	const bytes = new Uint8Array(len)
	for (let i = 0; i < len; i++) {
		bytes[i] = binaryString.charCodeAt(i)
	}
	return bytes
}
